import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { Departure, DetailedStop, OtpUtil, Time } from '@ridango/components';
import { DepartureArrivalTime, AppStartupService, BaseComponent, TimeUtil } from '@ridango/core';
import { Temporal } from '@js-temporal/polyfill';
import { DatePipe } from '@angular/common';
import Env from '@ridango/core/env';
import { timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'rwidget-departure-routes-list',
    templateUrl: './departure-routes-list.component.html',
    styleUrls: ['./departure-routes-list.component.scss']
})
export class DepartureRoutesListComponent extends BaseComponent implements OnInit {
    @Input() showPatchSize: number;
    @Input() isStopShort = false;
    @Input() stop: DetailedStop;
    @Input() departures: Departure[] = [];
    @Input() departTimeFilter: DepartureArrivalTime;
    @Input() isLoadingDepartures: boolean;
    @Output() departureSelect: EventEmitter<Departure> = new EventEmitter<Departure>();

    public showingDepartureGroups: { date: string; departures: Departure[] }[] = [];
    public isGroupedRoutes = false;
    private loadMoreClickedTimes = 1;
    private timezone: string = this.appStartup.getTimezone();
    otpUtil = OtpUtil;

    constructor(
        @Inject('ENV') public env: Env,
        private appStartup: AppStartupService,
        private datePipe: DatePipe
    ) {
        super();
    }

    ngOnInit() {
        this.isGroupedRoutes = this.env.EXPLORE?.SHOW_GROUPED_STOP_ROUTES && !this.isStopShort;
        timer(0, 2 * TimeUtil.MILLISECONDS_IN_SECOND)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => this.updateShowingDepartures());
    }

    private getDepartureIsDateNotInPast(departure: Departure): boolean {
        const depTime = departure.time.realtimeDeparture || departure.time.scheduledDeparture;
        let depEpoch: number;
        if (typeof depTime === 'string') {
            depEpoch = Temporal.Instant.from(depTime).epochSeconds;
        } else {
            depEpoch = depTime;
        }
        const nowEpoch = Temporal.Now.instant().epochSeconds;
        const isNotInPast = depEpoch >= nowEpoch;
        departure.isDateNotInPast = isNotInPast;
        return isNotInPast;
    }

    emitTripClick(departure: Departure) {
        this.departureSelect.emit(departure);
    }

    loadMoreDepartures() {
        this.loadMoreClickedTimes++;
        this.updateShowingDepartures();
    }

    loadAllDepartures() {
        this.showPatchSize = undefined;
        this.updateShowingDepartures();
    }

    showLoadMoreButton(): boolean {
        if (this.env.EXPLORE?.DEPARTURES_REQUEST_MAX_NO_PATCHING) return false;
        const patchingIsEnabled = this.showPatchSize !== undefined;
        const departureListHasMoreItems = this.getShowNumberOfElements() < this.departures.length;
        return patchingIsEnabled && departureListHasMoreItems;
    }

    private groupDepartureByDayDate(departures: Departure[]): { date: string; departures: Departure[] }[] {
        departures.forEach((dep) => this.getDepartureIsDateNotInPast(dep));
        const departuresWithValidDate: Departure[] = departures.map((departure) => ({
            ...departure,
            time: {
                ...departure.time,
                departureTime: departure.time.scheduledDeparture
            }
        }));

        const groupedDepartures = departuresWithValidDate.reduce((acc, departure) => {
            let date = this.datePipe.transform(
                this.getZonedMilliseconds(departure?.time.scheduledDeparture),
                this.env.PRIMARY_DATE_FORMAT
            );

            date = this.setDate(date, departure);
            acc[date] = acc[date] || [];
            acc[date].push(departure);
            return acc;
        }, {});

        const result = Object.keys(groupedDepartures).map((date) => ({ date, departures: groupedDepartures[date] }));
        const sortedResultByDate = result.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateA.getTime() - dateB.getTime();
        });

        const sortedResultByTime = sortedResultByDate.map((group) => {
            const sortedDepartures = this.sortDeparturesByTime(group.departures);
            return { date: group.date, departures: sortedDepartures };
        });

        return sortedResultByTime;
    }

    private setDate(date: string, departure: Departure): string {
        const departureInstant = Temporal.Instant.from(departure.time.scheduledDeparture as string);
        const departureDate = departureInstant.toZonedDateTimeISO(this.timezone).toPlainDate();
        const isSameDate = departureDate.equals(Temporal.Now.plainDateISO());

        if (isSameDate) {
            return '';
        }

        return this.datePipe.transform(date, this.env.PRIMARY_DATE_FORMAT_NGPIPE);
    }

    private sortDeparturesByTime(departures: Departure[]): Departure[] {
        return departures.sort((a, b) =>
            this.getScheduledDepartureTime(a.time) > this.getScheduledDepartureTime(b.time) ? 1 : -1
        );
    }

    private updateShowingDepartures() {
        //FIXME: move logic to BE. Retrieve already sorted departures before patching in FE. Jira: STW-921
        this.departures.forEach((dep) => this.getDepartureIsDateNotInPast(dep));
        if (!this.env.EXPLORE?.SORT_DEPARTURE_LIST) {
            this.sortDepartures();
        }
        let departuresToBeShown = this.departures.filter((dep) => dep.isDateNotInPast);
        if (this.showPatchSize !== undefined) {
            departuresToBeShown = departuresToBeShown.slice(0, this.getShowNumberOfElements());
        }
        const departuresGroupedByDate = this.groupDepartureByDayDate(departuresToBeShown);
        this.showingDepartureGroups = this.sortDeparturesByEarliestMainDisplayTime(departuresGroupedByDate);
    }
    private addDelay(plannedTime, delayDuration: number) {
        let inst =
            typeof plannedTime === 'string'
                ? Temporal.Instant.from(plannedTime)
                : Temporal.Instant.fromEpochSeconds(plannedTime);

        if (delayDuration != null) {
            inst = inst.add({ seconds: delayDuration });
        }
        const zoned = inst.toZonedDateTimeISO(this.timezone).round('minute');
        return new Date(zoned.toString());
    }

    private sortDeparturesByEarliestMainDisplayTime(departureGroups) {
        departureGroups.forEach((group) => {
            group.departures.sort((a, b) => {
                const getMainDisplayTime = (tripDeparture) => {
                    const plannedTime = tripDeparture.time.scheduledDeparture;
                    const prognosisTime = this.addDelay(plannedTime, tripDeparture.time.departureDelay);

                    if (tripDeparture.time.timepoint) {
                        return tripDeparture.time.realtime && tripDeparture.time.departureDelay != null
                            ? prognosisTime
                            : new Date(plannedTime);
                    }

                    if (
                        !tripDeparture.time.realtime ||
                        tripDeparture.time.departureDelay == null ||
                        tripDeparture.time.departureDelay === 0
                    ) {
                        return new Date(plannedTime);
                    }

                    const diffMinutes = Math.floor(Math.abs(tripDeparture.time.departureDelay / 60));
                    const threshold = this.env?.APPROXIMATE_TIME_THRESHOLD_MINUTES;

                    if (threshold && diffMinutes >= threshold) {
                        return prognosisTime;
                    } else {
                        return new Date(plannedTime);
                    }
                };
                return getMainDisplayTime(a).getTime() - getMainDisplayTime(b).getTime();
            });
        });

        return departureGroups;
    }

    private getShowNumberOfElements(): number {
        if (this.showPatchSize === undefined) {
            return this.departures.length;
        }
        return this.showPatchSize * this.loadMoreClickedTimes;
    }

    private sortDepartures() {
        const timeZone = this.appStartup.getTimezone();
        const milliseconds = Temporal.Now.instant().toZonedDateTimeISO(timeZone).epochMilliseconds;

        this.departures.sort((a, b) => {
            const timeA = this.getZonedMilliseconds(a.time.realtimeDeparture || a.time.scheduledDeparture);
            const timeB = this.getZonedMilliseconds(b.time.realtimeDeparture || b.time.scheduledDeparture);

            return timeA >= milliseconds && timeB < milliseconds
                ? -1
                : timeA < milliseconds && timeB >= milliseconds
                  ? 1
                  : timeA - timeB;
        });
    }

    getZonedMilliseconds = (time: number | string): number => {
        const timeZone = this.appStartup.getTimezone();
        if (!time) {
            return 0;
        }
        return typeof time === 'string'
            ? Temporal.Instant.from(time).toZonedDateTimeISO(timeZone).epochMilliseconds
            : Temporal.Instant.fromEpochSeconds(time).toZonedDateTimeISO(timeZone).epochMilliseconds;
    };

    trackByTripId(_: number, departure: Departure): string {
        return departure.time.tripId;
    }

    trackByDepartureGroup(_: number, group: { date: string; departures: Departure[] }): string | number {
        return group.date;
    }

    getScheduledDepartureTime(time: Time): number {
        if (time.realtimeDeparture && typeof time.realtimeDeparture === 'string') {
            return this.getZonedMilliseconds(time.realtimeDeparture);
        } else if (time.scheduledDeparture && typeof time.scheduledDeparture === 'string') {
            return this.getZonedMilliseconds(time.scheduledDeparture);
        } else if (typeof time.scheduledDeparture === 'number') {
            return this.getZonedMilliseconds(time.serviceDay + time.scheduledDeparture);
        }
    }
}
